import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';

// ============ DISPATCH RECORDS (inside Transport tab) ============
export const DispatchRecords = ({ transporters = [] }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [fFrom, setFFrom] = useState(todayISO());
  const [fTo, setFTo] = useState(todayISO());
  const [fTransporter, setFTransporter] = useState('');
  const [fVehicle, setFVehicle] = useState('');
  const [fDriver, setFDriver] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'transporter' | 'vehicle'
  const [sortDir, setSortDir] = useState('desc');

  const load = async () => {
    setLoading(true);
    try {
      const res = await orderAPI.listDispatches({ status: 'all', limit: 2000 });
      setRows(res.data);
    } catch (e) { toast.error('Failed to load dispatches'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fmtDateTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };
  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { const d = new Date(iso); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`; }
    catch { return '-'; }
  };

  // Match a dispatch to a transporter by vehicle or driver
  const transporterFor = (r) => {
    for (const t of transporters) {
      const vmatch = (t.vehicles || []).some(v => v.number === r.vehicle_number);
      const dmatch = (t.drivers || []).some(d => d.phone === r.driver_mobile);
      if (vmatch || dmatch) return t;
    }
    return null;
  };

  const filtered = rows.filter(r => {
    const d = (r.dispatched_at || '').slice(0, 10);
    if (fFrom && d && d < fFrom) return false;
    if (fTo && d && d > fTo) return false;
    if (fTransporter) {
      const t = transporterFor(r);
      if (!t || t.id !== fTransporter) return false;
    }
    if (fVehicle && !(r.vehicle_number || '').toLowerCase().includes(fVehicle.toLowerCase())) return false;
    if (fDriver && !(r.driver_mobile || '').includes(fDriver)) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va = '', vb = '';
    if (sortBy === 'transporter') {
      va = (transporterFor(a)?.name || '\uFFFF').toLowerCase();
      vb = (transporterFor(b)?.name || '\uFFFF').toLowerCase();
    } else if (sortBy === 'vehicle') {
      va = (a.vehicle_number || '').toLowerCase();
      vb = (b.vehicle_number || '').toLowerCase();
    } else {
      va = a.dispatched_at || '';
      vb = b.dispatched_at || '';
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };
  const sortArrow = (col) => sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Summary
  const totalBags = sorted.reduce((s, r) => s + (r.items || []).reduce((a, i) => a + (i.quantity || 0), 0), 0);
  const uniqueOrders = new Set(sorted.map(r => r.order_id)).size;

  const exportCSV = () => {
    const headers = ['Date', 'Transporter', 'Dealer', 'Vehicle', 'Driver', 'Facility', 'Items', 'Bags', 'Status'];
    const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = [headers.join(',')];
    sorted.forEach(r => {
      const t = transporterFor(r);
      const bags = (r.items || []).reduce((a, i) => a + (i.quantity || 0), 0);
      const itemsStr = (r.items || []).map(i => `${i.product_name}x${i.quantity}`).join('; ');
      lines.push([fmtDateTime(r.dispatched_at), t?.name || '-', r.dealer_name, r.vehicle_number, r.driver_mobile, r.facility || '(set at delivery)', itemsStr, bags, r.delivered_at ? 'Delivered' : 'In Transit'].map(esc).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `dispatch-records-${fFrom}-to-${fTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="dispatch-records-section">
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div>
          <div className="text-[11px] mb-1" style={{ color: '#5C6B61' }}>From</div>
          <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="w-[160px]" data-testid="dr-from" />
        </div>
        <div>
          <div className="text-[11px] mb-1" style={{ color: '#5C6B61' }}>To</div>
          <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="w-[160px]" data-testid="dr-to" />
        </div>
        <div>
          <div className="text-[11px] mb-1" style={{ color: '#5C6B61' }}>Transporter</div>
          <select value={fTransporter} onChange={(e) => setFTransporter(e.target.value)} className="border rounded px-2 py-2 text-sm min-w-[180px]" style={{ borderColor: '#E2DFD8' }} data-testid="dr-transporter">
            <option value="">All transporters</option>
            {transporters.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[11px] mb-1" style={{ color: '#5C6B61' }}>Vehicle #</div>
          <Input value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} placeholder="RJ01AB..." className="w-[140px]" data-testid="dr-vehicle" />
        </div>
        <div>
          <div className="text-[11px] mb-1" style={{ color: '#5C6B61' }}>Driver</div>
          <Input value={fDriver} onChange={(e) => setFDriver(e.target.value)} placeholder="Phone" className="w-[140px]" data-testid="dr-driver" />
        </div>
        <Button variant="outline" size="sm" onClick={() => { setFFrom(todayISO()); setFTo(todayISO()); setFTransporter(''); setFVehicle(''); setFDriver(''); }} data-testid="dr-reset">Today</Button>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={sorted.length === 0} data-testid="dr-export">
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Card className="p-3"><div className="text-xs" style={{ color: '#5C6B61' }}>Dispatches</div><div className="text-2xl font-bold">{sorted.length}</div></Card>
        <Card className="p-3"><div className="text-xs" style={{ color: '#5C6B61' }}>Orders</div><div className="text-2xl font-bold" style={{ color: '#2B5339' }}>{uniqueOrders}</div></Card>
        <Card className="p-3"><div className="text-xs" style={{ color: '#5C6B61' }}>Bags</div><div className="text-2xl font-bold" style={{ color: '#2B5339' }}>{totalBags.toLocaleString('en-IN')}</div></Card>
        <Card className="p-3"><div className="text-xs" style={{ color: '#5C6B61' }}>Delivered</div><div className="text-2xl font-bold">{sorted.filter(r => r.delivered_at).length}<span className="text-sm font-normal" style={{ color: '#5C6B61' }}> / {sorted.length}</span></div></Card>
      </div>

      <Card className="overflow-auto">
        <table className="w-full text-xs" data-testid="dr-table">
          <thead>
            <tr style={{ background: '#FBF9F6' }}>
              <th className="p-2 text-left cursor-pointer select-none hover:bg-[#F5F1E8]" onClick={() => toggleSort('date')} data-testid="dr-sort-date">Date{sortArrow('date')}</th>
              <th className="p-2 text-left cursor-pointer select-none hover:bg-[#F5F1E8] whitespace-nowrap min-w-[180px]" onClick={() => toggleSort('transporter')} data-testid="dr-sort-transporter">Transporter{sortArrow('transporter')}</th>
              <th className="p-2 text-left whitespace-nowrap min-w-[180px]">Dealer</th>
              <th className="p-2 text-left cursor-pointer select-none hover:bg-[#F5F1E8] whitespace-nowrap min-w-[140px]" onClick={() => toggleSort('vehicle')} data-testid="dr-sort-vehicle">Vehicle{sortArrow('vehicle')}</th>
              <th className="p-2 text-left">Driver</th>
              <th className="p-2 text-left">Items</th>
              <th className="p-2 text-right">Bags</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="p-6 text-center"><Loader2 className="inline w-4 h-4 animate-spin" /></td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan="8" className="p-6 text-center" style={{ color: '#5C6B61' }}>No dispatches in this range.</td></tr>
            ) : sorted.map(r => {
              const t = transporterFor(r);
              const bags = (r.items || []).reduce((a, i) => a + (i.quantity || 0), 0);
              return (
                <tr key={r.dispatch_id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="dr-row">
                  <td className="p-2 whitespace-nowrap">{fmtDate(r.dispatched_at)}<div className="text-[10px]" style={{ color: '#5C6B61' }}>{fmtDateTime(r.dispatched_at).split(',')[1] || ''}</div></td>
                  <td className="p-2 whitespace-nowrap">{t?.name || <span style={{ color: '#5C6B61' }}>-</span>}</td>
                  <td className="p-2 whitespace-nowrap">{r.dealer_name}</td>
                  <td className="p-2 whitespace-nowrap">{r.vehicle_number || '-'}</td>
                  <td className="p-2">{r.driver_mobile || '-'}</td>
                  <td className="p-2" style={{ color: '#5C6B61' }}>{(r.items || []).map(i => `${i.product_name}×${i.quantity}`).join(', ')}</td>
                  <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }}>{bags}</td>
                  <td className="p-2">
                    {r.delivered_at ? (
                      <span className="text-[10px] font-semibold" style={{ color: '#2B5339' }}>DELIVERED</span>
                    ) : (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#E89D4F', color: 'white' }}>IN TRANSIT</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

